import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { 
  Star, CheckCircle2, XCircle, ChevronRight, 
  Award, TrendingDown, Filter, ArrowUpDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PublicLayout from "@/components/layout/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoryPage({ slug }) {
  const params = useParams();
  const categorySlug = slug || params.slug;
  
  const [category, setCategory] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("featured");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [tariffsRes, faqsRes] = await Promise.all([
          axios.get(`${API}/public/tariffs/${categorySlug}`),
          axios.get(`${API}/public/faqs/${categorySlug}`),
        ]);
        setCategory(tariffsRes.data.category);
        setTariffs(tariffsRes.data.tariffs);
        setFaqs(faqsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categorySlug]);

  const sortedTariffs = [...tariffs].sort((a, b) => {
    switch (sortBy) {
      case "price-asc":
        return a.price - b.price;
      case "price-desc":
        return b.price - a.price;
      case "rating":
        return b.rating - a.rating;
      default:
        return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
    }
  });

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`w-4 h-4 ${i < Math.floor(rating) ? "text-yellow-400 fill-yellow-400" : "text-slate-300"}`} 
          />
        ))}
        <span className="ml-1 text-sm font-medium text-slate-700">{rating.toFixed(1)}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-12 w-2/3 bg-slate-200 rounded mb-4"></div>
            <div className="h-6 w-1/2 bg-slate-200 rounded mb-8"></div>
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-slate-200 rounded-xl mb-4"></div>
            ))}
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!category) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-navy-900">Kategorie nicht gefunden</h1>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <Badge className="mb-4 bg-electric-100 text-electric-700 hover:bg-electric-100">
              Vergleich 2026
            </Badge>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mb-4">
              {category.meta_title || `${category.name} vergleichen`}
            </h1>
            <p className="text-lg text-slate-600 mb-6">
              {category.meta_description || category.description}
            </p>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-signal-500" />
                <span>{tariffs.length} Tarife verfügbar</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-signal-500" />
                <span>Ab {Math.min(...tariffs.map(t => t.price)).toFixed(2)} €</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Sort */}
      <section className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200 py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Filter className="w-4 h-4" />
              <span>{sortedTariffs.length} Ergebnisse</span>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">Sortieren:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]" data-testid="sort-select">
                  <SelectValue placeholder="Sortierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Empfohlen</SelectItem>
                  <SelectItem value="price-asc">Preis aufsteigend</SelectItem>
                  <SelectItem value="price-desc">Preis absteigend</SelectItem>
                  <SelectItem value="rating">Beste Bewertung</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Tariffs List */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="space-y-4">
            {sortedTariffs.map((tariff, index) => (
              <Card 
                key={tariff.id} 
                className={`tariff-card overflow-hidden border ${tariff.is_testsieger ? "border-yellow-400 ring-2 ring-yellow-100" : "border-slate-200"}`}
                data-testid={`tariff-card-${tariff.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    {/* Provider Info */}
                    <div className="lg:w-48 p-6 bg-slate-50 flex flex-col items-center justify-center border-b lg:border-b-0 lg:border-r border-slate-200">
                      {tariff.is_testsieger && (
                        <Badge className="mb-2 bg-yellow-400 text-yellow-900 hover:bg-yellow-400">
                          <Award className="w-3 h-3 mr-1" />
                          Testsieger
                        </Badge>
                      )}
                      {tariff.provider?.logo_url ? (
                        <img 
                          src={tariff.provider.logo_url} 
                          alt={tariff.provider?.name || "Provider"} 
                          className="h-12 w-auto object-contain mb-2"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'block';
                          }}
                        />
                      ) : null}
                      <span className={`text-lg font-bold text-navy-900 ${tariff.provider?.logo_url ? 'hidden' : ''}`}>
                        {tariff.provider?.name || "Anbieter"}
                      </span>
                      <span className="text-sm text-slate-500 mt-1">{tariff.name}</span>
                    </div>

                    {/* Tariff Details */}
                    <div className="flex-1 p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
                            {tariff.name}
                          </h3>
                          
                          {/* Features */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {tariff.speed && (
                              <Badge variant="outline" className="text-xs">
                                {tariff.speed}
                              </Badge>
                            )}
                            {tariff.data_volume && (
                              <Badge variant="outline" className="text-xs">
                                {tariff.data_volume}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {tariff.contract_duration}
                            </Badge>
                          </div>
                          
                          {/* Feature List */}
                          <ul className="text-sm text-slate-600 space-y-1">
                            {tariff.features.slice(0, 3).map((feature, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-signal-500 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                          
                          {/* Rating */}
                          <div className="mt-3">
                            {renderStars(tariff.rating)}
                            <span className="text-xs text-slate-500 ml-2">
                              ({tariff.rating_count.toLocaleString()} Bewertungen)
                            </span>
                          </div>
                        </div>

                        {/* Price & CTA */}
                        <div className="lg:text-right lg:min-w-[200px]">
                          {tariff.bonus && (
                            <Badge className="mb-2 bg-signal-100 text-signal-700 hover:bg-signal-100">
                              {tariff.bonus}
                            </Badge>
                          )}
                          
                          <div className="text-3xl font-bold text-navy-900">
                            {tariff.price.toFixed(2).replace(".", ",")} <span className="text-base font-normal text-slate-500">{tariff.price_unit}</span>
                          </div>
                          
                          {tariff.original_price && (
                            <div className="text-sm text-slate-500 line-through">
                              {tariff.original_price.toFixed(2).replace(".", ",")} €
                            </div>
                          )}
                          
                          <Button 
                            className="mt-4 w-full lg:w-auto bg-electric-500 hover:bg-electric-600 text-white rounded-full px-8 shadow-button"
                            data-testid={`tariff-cta-${tariff.id}`}
                            onClick={async () => {
                              // Track click
                              try {
                                await axios.post(`${API}/analytics/click?tariff_id=${tariff.id}&category_id=${category.id}`);
                              } catch (e) {
                                console.error("Click tracking failed:", e);
                              }
                              
                              // Open affiliate link or provider website
                              if (tariff.affiliate_link) {
                                window.open(tariff.affiliate_link, "_blank");
                              } else if (tariff.provider?.website) {
                                window.open(tariff.provider.website, "_blank");
                              }
                            }}
                          >
                            Zum Anbieter
                            <ChevronRight className="ml-2 w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Pros & Cons */}
                      {(tariff.pros?.length > 0 || tariff.cons?.length > 0) && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {tariff.pros?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-signal-600 mb-2">Vorteile</h4>
                                <ul className="space-y-1">
                                  {tariff.pros.map((pro, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                      <CheckCircle2 className="w-4 h-4 text-signal-500 flex-shrink-0" />
                                      {pro}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {tariff.cons?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-red-600 mb-2">Nachteile</h4>
                                <ul className="space-y-1">
                                  {tariff.cons.map((con, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                      <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                      {con}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose Guide */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-navy-900 mb-6">
              So wählen Sie den richtigen Tarif
            </h2>
            
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600">
                Bei der Auswahl des richtigen Tarifs sollten Sie folgende Punkte beachten:
              </p>
              
              <ol className="list-decimal list-inside space-y-3 text-slate-600">
                <li><strong>Preis-Leistungs-Verhältnis:</strong> Vergleichen Sie nicht nur den Preis, sondern auch die enthaltenen Leistungen.</li>
                <li><strong>Vertragslaufzeit:</strong> Kürzere Laufzeiten bieten mehr Flexibilität, längere oft günstigere Preise.</li>
                <li><strong>Bonus & Rabatte:</strong> Achten Sie auf Neukundenboni und Wechselprämien.</li>
                <li><strong>Kundenbewertungen:</strong> Die Erfahrungen anderer Nutzer können bei der Entscheidung helfen.</li>
                <li><strong>Service & Support:</strong> Ein guter Kundenservice ist bei Problemen wichtig.</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-12" itemScope itemType="https://schema.org/FAQPage">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-navy-900 mb-6">
                Häufig gestellte Fragen
              </h2>
              
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem 
                    key={faq.id} 
                    value={`faq-${index}`}
                    className="bg-white border border-slate-200 rounded-xl px-6"
                    itemScope 
                    itemProp="mainEntity" 
                    itemType="https://schema.org/Question"
                  >
                    <AccordionTrigger className="text-left font-semibold text-navy-900 hover:no-underline py-4">
                      <span itemProp="name">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent 
                      className="text-slate-600 pb-4"
                      itemScope 
                      itemProp="acceptedAnswer" 
                      itemType="https://schema.org/Answer"
                    >
                      <span itemProp="text">{faq.answer}</span>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* Affiliate Disclosure */}
      <section className="py-6 bg-slate-100 border-t border-slate-200">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-slate-500 max-w-3xl mx-auto">
            <strong>Hinweis:</strong> Die auf dieser Seite dargestellten Tarife enthalten 
            Affiliate-Links. Bei Abschluss eines Vertrags erhalten wir möglicherweise eine 
            Provision vom Anbieter. Der Preis für Sie bleibt unverändert.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
